import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';

export interface RevisionFilter {
  showOnlyMine: boolean;
  showOnlyMe: boolean;
  showCompleted: boolean;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
}

export const defaultFilterModel: GridFilterModel = {
  items: [{ id: 'completedAtDefault', field: 'completedAt', operator: 'isEmpty' }],
};

export const defaultSortModel: GridSortModel = [
  {
    field: 'progress',
    sort: 'desc',
  },
  {
    field: 'lastActivityAt',
    sort: 'desc',
  },
];

const initialState: RevisionFilter = {
  showOnlyMine: false,
  showOnlyMe: false,
  showCompleted: false,
  filterModel: defaultFilterModel,
  sortModel: defaultSortModel,
};

export const revisionFilterSlice = createSlice({
  name: 'revisionFilter',
  initialState,
  reducers: {
    showOnlyMine: (state, action: PayloadAction<boolean>) => {
      state.showOnlyMine = action.payload;
    },
    showOnlyMe: (state, action: PayloadAction<boolean>) => {
      state.showOnlyMe = action.payload;
    },
    showCompleted: (state, action: PayloadAction<boolean>) => {
      state.showCompleted = action.payload;
    },
    setFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      // The grid re-emits an equal model as a new object; storing it would re-render and loop.
      if (isDeepEqual(state.filterModel, action.payload)) return;
      state.filterModel = action.payload;
    },
    setSortModel: (state, action: PayloadAction<GridSortModel>) => {
      if (isDeepEqual(state.sortModel, action.payload)) return;
      state.sortModel = action.payload;
    },
    resetFilterModel: (state) => {
      state.filterModel = defaultFilterModel;
      state.sortModel = defaultSortModel;
    },
  },
});

export const isDefaultFilterModel = (model: GridFilterModel) =>
  isDeepEqual(model.items ?? [], defaultFilterModel.items) && (model.quickFilterValues ?? []).length === 0;

export const isDefaultSortModel = (model: GridSortModel) => isDeepEqual(model, defaultSortModel);

function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aKeys = Object.keys(a).filter((key) => (a as never)[key] !== undefined);
  const bKeys = Object.keys(b).filter((key) => (b as never)[key] !== undefined);
  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => isDeepEqual((a as never)[key], (b as never)[key]));
}

export const { showOnlyMine, showOnlyMe, showCompleted, setFilterModel, setSortModel, resetFilterModel } = revisionFilterSlice.actions;

export const selectRevisionFilter = (state: RootState) => state.revisionFilter;

export default revisionFilterSlice.reducer;
